'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import ColorPickerArea from './ColorPickerArea';
import HuePicker from './HuePicker';
import ColorValues from './ColorValues';
import { hexToRgb, rgbToHsv } from './colorUtils';
import { Button } from '@mui/material';
import { constants } from '../../constants';
const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    width: '364px',
    padding: '24px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
    flexShrink: 0,
    borderRadius: '12px',
    border: '1px solid #EBEBEB',
    boxShadow: 'none',
    fontFamily: '"Open Sans", -apple-system, Helvetica, sans-serif',
    backgroundColor: '#fff',
    [theme.breakpoints.down('md')]: {
        width: '100%',
        maxWidth: '364px',
        margin: '0 auto'
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: 'auto',
        padding: '12px',
        gap: '12px'
    }
}));
const PickerFull = ({ setColor, defaultColor }) => {
    const [initialHex] = useState(defaultColor ?? '#E89623');
    const initialRgb = hexToRgb(initialHex);
    const initialHsv = rgbToHsv(initialRgb.r, initialRgb.g, initialRgb.b);
    const [currentColor, setCurrentColor] = useState({
        hex: initialHex,
        rgb: initialRgb,
        hsv: initialHsv
    });
    // Calculate initial positions based on HSV values
    const initialSaturationX = (initialHsv.s / 100) * 319;
    const initialBrightnessY = 238 - (initialHsv.v / 100) * 238;
    const initialHueX = (initialHsv.h / 360) * 320;
    const [pickerPosition, setPickerPosition] = useState({
        x: initialSaturationX,
        y: initialBrightnessY
    });
    const [huePosition, setHuePosition] = useState(initialHueX);
    useEffect(() => {
        // Update positions when color changes externally
        const saturationX = (currentColor.hsv.s / 100) * 319;
        const brightnessY = 238 - (currentColor.hsv.v / 100) * 238;
        const hueX = (currentColor.hsv.h / 360) * 320;
        setPickerPosition({ x: saturationX, y: brightnessY });
        setHuePosition(hueX);
    }, [currentColor.hsv]);
    useEffect(() => {
        // Update current color when defaultColor changes
        if (defaultColor) {
            const rgb = hexToRgb(defaultColor);
            const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
            setCurrentColor({
                hex: defaultColor,
                rgb,
                hsv
            });
            const saturationX = (hsv.s / 100) * 319;
            const brightnessY = 238 - (hsv.v / 100) * 238;
            const hueX = (hsv.h / 360) * 320;
            setPickerPosition({ x: saturationX, y: brightnessY });
            setHuePosition(hueX);
        }
    }, [defaultColor]);
    const handleColorChange = (newColor) => {
        setCurrentColor(newColor);
    };
    const handlePickerPositionChange = (position) => {
        setPickerPosition(position);
    };
    const handleHuePositionChange = (position) => {
        setHuePosition(position);
    };
    return (_jsxs(Container, { as: "section", "aria-label": "Color Picker", children: [_jsx(ColorPickerArea, { currentColor: currentColor, onChange: handleColorChange, pickerPosition: pickerPosition, onPositionChange: handlePickerPositionChange }), _jsx(HuePicker, { currentColor: currentColor, onChange: handleColorChange, huePosition: huePosition, onHuePositionChange: handleHuePositionChange }), _jsx(ColorValues, { values: currentColor, onChange: handleColorChange }), _jsx(Button, { fullWidth: true, onClick: () => {
                    setColor(currentColor.hex);
                }, color: "primary", variant: "contained", children: constants.apply })] }));
};
export default PickerFull;
